import React, { useState, useEffect, useRef }  from 'react';
import { ContentPopup } from 'components/ContentPopup.jsx';
import TechnicalPropertyOptionRow from 'components/show_drawing/redraw_form/technical_properties/TechnicalPropertyOptionRow.jsx';
import styles from 'components/show_drawing/redraw_form/_style.module.scss';
import Constants from 'components/show_drawing/Constants.jsx';
import technicalPropertyCombinationSignature from 'components/show_drawing/redraw_form/technicalPropertyCombinationSignature.js';

// Lists the drawing options an admin flagged as technical properties
// (DrawingOption#technical_property) in a popup that deliberately looks and
// feels identical to the tag-based technical-properties popup
// (TechnicalProperties.jsx) — same wide two-column modal, "Technical
// properties" title, row list and "Confirm technical properties" button — so
// the customer sees one familiar popup, not two different ones.
//
// Unlike a normal option, picking a value here does NOT redraw the dieline:
// the rows stage their choice in the form (redrawOnChange is off) but the
// main drawing only recomputes when "Confirm technical properties" is pressed
// — matching the tag popup, where the choice is a candidate until confirmed.
// Closing with the × instead reverts each option to its committed value
// (from currentDrawingOptions), so a cancelled pick cannot leak into a later
// redraw.
//
// The ContentPopup is mounted whenever any such option exists; opening only
// toggles its display (popup.css .modal is display:none by default). It stays
// mounted while closed on purpose: its inputs must remain inside
// <form ref={formRef}> so every redraw keeps sending the committed technical
// properties, even when the popup is shut.
//
// The right-hand pane previews the dieline for the WHOLE combination the customer
// has selected across the technical-property selects (e.g. production_method +
// piececount + glueonw): each combination's black-background SVG is pre-rendered
// (UpdateDrawingTechnicalPropertyImagesJob) and reaches the client as the
// technicalPropertyImages map { combination signature => url }. Each row reports
// its current value; the pane looks the image up by the combined signature.
const TechnicalPropertyOptions = ({ id, drawing, unit, options, technicalPropertyImages, optionsUserChanged, addToOptionsUserChanged, removeFromOptionsUserChanged, setRedrawRequested, currentDrawingOptions }) => {
  const popupRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [combination, setCombination] = useState({});
  const [previewedSvg, setPreviewedSvg] = useState(null);
  // The set of options the customer had already changed when the popup opened,
  // so cancel can tell a genuine prior change apart from one it is about to undo.
  const optionsUserChangedOnOpen = useRef([]);

  // Each row reports its current value here; together they form the combination
  // whose preview the pane shows.
  const setCombinationValue = (name, value) => {
    setCombination((previous) => ({ ...previous, [name]: value }));
  };

  // The stored preview for the current combination, keyed by the same signature
  // the server stores it under (Drawing#technical_property_combination_signature).
  const signature = technicalPropertyCombinationSignature(options.map((option) => option.name), combination);
  const previewedImage = technicalPropertyImages ? technicalPropertyImages[signature] : undefined;

  // Fetch the combination's stored SVG and inject it inline, the way the rest of
  // the app renders its black-theme dielines (drawings/images fetchAndRenderSvg):
  // these SVGs carry the inline (home/svg) XHTML root namespace, so an <img>
  // would XML-parse them as non-SVG and show nothing — injecting the markup lets
  // the HTML parser render it and the page's theme CSS apply.
  //
  // The rendered dieline's root carries id="svg", which is exactly the element
  // DrawingSvg's svg-pan-zoom binds to (getSvgPanZoom('#svg')). Injecting it
  // verbatim would put a second #svg on the page; svg-pan-zoom then fits THIS
  // hidden, zero-size copy and throws "matrix is not invertible", crashing the
  // whole DrawingSvg tree. Drop the colliding id before injecting.
  useEffect(() => {
    if (!previewedImage) { setPreviewedSvg(null); return; }
    let cancelled = false;
    fetch(previewedImage)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((svg) => { if (!cancelled) { setPreviewedSvg(svg.replace('id="svg"', '')); } })
      .catch(() => { if (!cancelled) { setPreviewedSvg(null); } });
    return () => { cancelled = true; };
  }, [previewedImage]);

  // Match the tag popup's modal geometry so the two are indistinguishable.
  const containerWidth = $('.container').width?.();
  const modalStyle = {
    width: containerWidth ? containerWidth * 0.92 : undefined,
    height: "96%",
    maxWidth: "unset"
  };

  // Mirrors TechnicalProperties.jsx#resize: keep the scrollable left column and
  // the flex row the height of the modal body. Scoped to this popup and guarded
  // so it is a no-op where jQuery is absent (tests) or the popup is closed.
  useEffect(() => {
    if (!isOpen) { return; }
    let timer;
    const resize = () => {
      try {
        const modalContentHeight = $(popupRef.current).find('.modal-content').height();
        if (modalContentHeight) {
          $('#technical_property_options').height(modalContentHeight - 40);
          $('#technical_property_options_left').height(modalContentHeight - 40);
        }
      } catch (error) { /* no jQuery / not rendered */ }
      timer = setTimeout(resize, 200);
    };
    resize();
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (options.length == 0) { return <></> }

  const setDisplay = (value) => {
    if (popupRef.current) { popupRef.current.style.display = value; }
  }

  const committedValueOf = (option) => {
    if (currentDrawingOptions && currentDrawingOptions[option.name] != null) {
      return currentDrawingOptions[option.name];
    }
    return option.default_value;
  }

  const open = (e) => {
    e.preventDefault();
    optionsUserChangedOnOpen.current = [...optionsUserChanged];
    setDisplay("block");
    setIsOpen(true);
  }

  // Commit the staged choices: the rows already hold them in the form, so one
  // redraw applies them all at once.
  const confirm = (e) => {
    e.preventDefault();
    setRedrawRequested(() => true);
    setDisplay("none");
    setIsOpen(false);
  }

  // Cancel: put every option back to its committed value without a redraw, so a
  // staged-but-unconfirmed pick does not survive the close. The revert is sent
  // with user_changed: false so OptionsCommon does not record it as a customer
  // edit, and any option that had been staged during this session (but was not
  // already user-changed when the popup opened) is un-marked — otherwise merely
  // opening and dismissing the popup would freeze an auto-calculated technical
  // property to its current value.
  const cancel = () => {
    options.forEach((option) => {
      emitEvent(Constants.drawing_option_set_value, { id: id, name: option.name, value: committedValueOf(option), redraw: false, user_changed: false });
      if (!optionsUserChangedOnOpen.current.includes(option.name)) {
        removeFromOptionsUserChanged(option);
      }
    });
    setIsOpen(false);
  }

  const content = (
    <div className={styles.tp_div} id="technical_property_options">
      <div className={styles.tp_left} id="technical_property_options_left">
        <div className={styles.tp_ags_as_options}>
          <div className={styles.tp_header}> <h4> {t('models.technical_properties')} </h4> </div>

          {options.map((option, index) => (
            <TechnicalPropertyOptionRow
              key={`${id}${option.name}${unit}`}
              id={id}
              drawing={drawing}
              option={option}
              unit={unit}
              optionsUserChanged={optionsUserChanged}
              addToOptionsUserChanged={addToOptionsUserChanged}
              setRedrawRequested={setRedrawRequested}
              isLast={index === options.length - 1}
              setCombinationValue={setCombinationValue}
            />
          ))}

          <br />
        </div>

        <center>
          <div className={styles.row}>
            <button onClick={confirm} className="btn simple-btn">
              &nbsp; {t('models.confirm_technical_properties')}
            </button>
            <br />
            <br />
          </div>
        </center>
      </div>

      <div className={styles.tp_right}>
        <div className="redraw_theme_black" style={{ minHeight: '600px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' }}>
          {previewedSvg
            ? <div style={{ width: '90%', maxHeight: '90%' }} dangerouslySetInnerHTML={{ __html: previewedSvg }} />
            : <span style={{ color: '#888' }}> {t('models.technical_property_preview_pending')} </span>}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={`${styles.row} ${styles.optionLink}`}>
        <span className={`input-group-addon input-group-addon-${window.locale} ${styles.input_add_on_for_technical_props}`}>
          <a href="#" onClick={open} className="btn simple-btn dieline-outline-btn">
            <i className="fa fa-pencil-square-o"></i> &nbsp; { t('models.change_technical_properties') }
          </a>
        </span>
      </div>

      <ContentPopup
        id="technical_property_options_modal"
        autoOpen={false}
        myRef={popupRef}
        content={content}
        cancelButton={false}
        beforeClose={cancel}
        modalStyle={modalStyle}
        notWide={true}
      />
    </>
  )
}

export default TechnicalPropertyOptions;
